import {proxyService} from "../../services/Interceptor"

// Couche réseau de la page d'enchères.
//
// Tout passe par le BFF same-origin (`/api/...`) : c'est lui qui détient le
// cookie de session et le traduit en Bearer pour l'API. Le jeton n'existe donc
// jamais en JavaScript — une faille d'injection sur le site de la synagogue ne
// permet pas d'enchérir au nom de quelqu'un.
const Ench = {
    get: (path, params, config) => proxyService.request({url: "/encheres" + path, method: "get", params, ...config}),
    post: (path, data, config) => proxyService.request({url: "/encheres" + path, method: "post", data, ...config}),
}

// L'identifiant de l'organisation accompagne chaque appel : les routes
// publiques ne sont pas authentifiées, et l'API doit savoir de quelle
// communauté il s'agit (elle le résout par id, préfixe ou domaine).
export function identifierOf(org) {
    return (org && (org.id || org.prefix || org.domain)) || null
}

export const EncheresAPI = {
    ventes: (identifier) => Ench.get(`/org/${encodeURIComponent(identifier)}`),
    vente: (identifier, venteId) => Ench.get(`/vente/${encodeURIComponent(identifier)}/${encodeURIComponent(venteId)}`),
    // `rev` = la révision que le client a déjà : l'API répond « rien de neuf »
    // au lieu de renvoyer toute la vente à chaque battement.
    live: (identifier, venteId, rev) => Ench.get(`/live/${encodeURIComponent(identifier)}/${encodeURIComponent(venteId)}`, rev ? {rev} : undefined),

    demanderCode: (identifier, data) => Ench.post("/session/code", {identifier, ...data}),
    verifierCode: (identifier, data) => Ench.post("/session/verifier", {identifier, ...data}),
    moi: (identifier) => Ench.get(`/session/moi/${encodeURIComponent(identifier)}`),
    profil: (identifier, data) => Ench.post("/session/profil", {identifier, ...data}),
    fermer: () => Ench.post("/session/fermer", {}),

    miser: (identifier, lotId, data) => Ench.post(`/lot/${encodeURIComponent(lotId)}/mise`, {identifier, ...data}),
    acheter: (identifier, lotId, data) => Ench.post(`/lot/${encodeURIComponent(lotId)}/achat`, {identifier, ...data}),
    beneficiaire: (identifier, lotId, data) => Ench.post(`/lot/${encodeURIComponent(lotId)}/beneficiaire`, {identifier, ...data}),

    creerGroupe: (identifier, lotId, data) => Ench.post(`/lot/${encodeURIComponent(lotId)}/groupe`, {identifier, ...data}),
    rejoindreGroupe: (identifier, token, data) => Ench.post(`/groupe/${encodeURIComponent(token)}/rejoindre`, {identifier, ...data}),
    voirGroupe: (identifier, token) => Ench.get(`/groupe/${encodeURIComponent(token)}`, {identifier}),
}

// Un hint local « je me suis identifié ici », pour éviter un clignotement entre
// l'affichage anonyme et l'affichage identifié au chargement. Ce n'est PAS la
// session (elle est dans le cookie HttpOnly) : au pire, il est faux et l'appel
// à `moi` rétablit la vérité.
const CLE_HINT = "ENCHERES_IDENTIFIE"

export function hintIdentifie() {
    if (typeof window === "undefined") return false
    try { return !!window.localStorage?.getItem(CLE_HINT) } catch (_) { return false }
}

export function poserHint(valeur) {
    if (typeof window === "undefined") return
    try {
        if (valeur) window.localStorage?.setItem(CLE_HINT, "1")
        else window.localStorage?.removeItem(CLE_HINT)
    } catch (_) { /* navigation privée : tant pis, `moi` tranchera */ }
}

// Montant lisible. Les enchères se lisent en un coup d'œil sur un téléphone :
// pas de décimales inutiles, la devise collée au montant.
export function formatMontant(montant, devise, lang = "fr") {
    if (montant === null || montant === undefined || !Number.isFinite(Number(montant))) return ""
    try {
        return new Intl.NumberFormat(lang === "he" ? "he-IL" : lang, {
            style: "currency", currency: String(devise || "eur").toUpperCase(),
            maximumFractionDigits: Number(montant) % 1 === 0 ? 0 : 2,
        }).format(Number(montant))
    } catch (_) {
        return `${montant} ${String(devise || "eur").toUpperCase()}`
    }
}
